import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Radio, Calendar, MapPin, Trophy, ArrowLeft } from "lucide-react";

const PB_LOGO = "/brand/pb-logo.png";
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const iv = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(iv); }, []);
  return <span className="font-mono tabular-nums">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>;
};

function MatchCard({ m, nameOf, heldIds }) {
  const live = m.status !== "complete" && m.court && m.side_a && m.side_b && !["BYE"].includes(m.side_a) && !["BYE"].includes(m.side_b);
  const done = m.status === "complete";
  const ready = !done && !live && m.side_a && m.side_b;
  const held = !done && heldIds && (heldIds.has(m.side_a) || heldIds.has(m.side_b));
  return (
    <div data-testid={`layout-match-${m.key}`}
      className={`border p-2.5 text-sm ${done ? "border-zinc-800 bg-zinc-950 opacity-60" : held ? "border-amber-500/60 bg-amber-500/5" : live ? "border-[#A3E635] bg-[#A3E635]/5" : "border-zinc-800 bg-zinc-950"}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="font-mono text-[10px] text-zinc-500">{m.key}</span>
        {held && <span className="text-[9px] font-black uppercase tracking-widest text-amber-400" data-testid={`layout-hold-${m.key}`}>⏸ On Hold — running late</span>}
        {!held && live && <span className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-[#A3E635]"><span className="w-1.5 h-1.5 rounded-full bg-[#A3E635] animate-pulse" /> Live · Court {m.court}</span>}
        {done && <span className="text-[9px] font-black uppercase tracking-widest text-zinc-600">Final</span>}
        {!held && ready && !live && <span className="text-[9px] font-black uppercase tracking-widest text-[#22D3EE]">{m.court ? `Court ${m.court}` : "Waiting for court"}</span>}
        {!held && !done && !live && !ready && <span className="text-[9px] uppercase tracking-widest text-zinc-700">TBD</span>}
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className={`truncate ${done && m.winner_id === m.side_a ? "font-bold text-white" : done ? "text-zinc-500" : "text-zinc-200"}`}>{nameOf(m.side_a)}</span>
        {done && <span className="font-mono font-bold text-zinc-300 shrink-0">{m.score_a}</span>}
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className={`truncate ${done && m.winner_id === m.side_b ? "font-bold text-white" : done ? "text-zinc-500" : "text-zinc-200"}`}>{nameOf(m.side_b)}</span>
        {done && <span className="font-mono font-bold text-zinc-300 shrink-0">{m.score_b}</span>}
      </div>
    </div>
  );
}

export default function CourtLayout() {
  const { tid } = useParams();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get(`/tournaments/${tid}`), api.get(`/tournaments/${tid}/court-board`)]);
      setData(a.data); setBoard(b.data); setError(null);
    } catch { setError("Tournament not found — are you signed in as the director?"); }
  }, [tid]);
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);

  if (error) return <div className="min-h-screen bg-black text-zinc-500 flex items-center justify-center font-display uppercase text-xl">{error}</div>;
  if (!data || !board) return <div className="min-h-screen bg-black text-zinc-500 flex items-center justify-center animate-pulse">Loading full layout…</div>;

  const { tournament: t, entries, matches, divisions } = data;
  const heldIds = new Set((entries || []).filter((e) => e.on_hold).map((e) => e.id));
  const nameOf = (id) => !id ? "TBD" : id === "BYE" ? "BYE" : (entries.find((e) => e.id === id)?.display_name || "TBD");
  const ca = t.court_assignments || {};
  const firstEv = divisions[0]?.event_type;
  const inDiv = (m, d) => (m.event_type || firstEv) === d.event_type && ((m.age_bracket || null) === (d.age_bracket || null)) && (!d.division || (m.division || null) === d.division);
  const divsWith = divisions.filter((d) => d.has_matches && matches.some((m) => inDiv(m, d)));
  const boardByCourt = {};
  (board.board || []).forEach((it) => { boardByCourt[it.court] = it; });
  const divsOnCourt = (c) => Object.entries(ca).filter(([, cs]) => (cs || []).includes(c)).map(([l]) => l);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="court-layout-page">
      <header className="border-b border-zinc-800 px-6 py-4 flex flex-wrap items-center justify-between gap-4 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="flex items-center gap-4">
          <img src={PB_LOGO} alt="" className="w-12 h-12 object-contain" />
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#22D3EE]">TD Full Layout</div>
            <h1 className="font-display font-black uppercase text-2xl md:text-3xl leading-none" data-testid="layout-title">{t.name}</h1>
            <div className="text-xs text-zinc-500 flex gap-4 mt-1">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{t.date}</span>
              {t.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t.location}</span>}
              <span className="flex items-center gap-1"><Trophy className="w-3 h-3" />{board.stats.matches_done}/{board.stats.matches_total} matches played</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-5">
          <a href={`/tournaments/${tid}`} data-testid="layout-back-link"
            className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Bracket Page
          </a>
          <span className="flex items-center gap-2 text-[#A3E635] text-xs font-black uppercase tracking-widest">
            <Radio className="w-4 h-4 animate-pulse" /> Live · updates every 10s
          </span>
          <span className="font-display font-black text-2xl text-white"><Clock /></span>
        </div>
      </header>

      <main className="p-6 space-y-10">
        <section data-testid="layout-courts-section">
          <div className="text-xs font-black uppercase tracking-[0.3em] text-[#A3E635] mb-3">Courts — Who's On Them Right Now</div>
          {(t.courts || []).length === 0 ? (
            <div className="text-zinc-600 border border-zinc-800 p-6 text-sm">No courts selected yet — check courts on the bracket page.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {(t.courts || []).map((c) => {
                const item = boardByCourt[c] || { court: c };
                const live = !!item.playing;
                const assigned = divsOnCourt(c);
                return (
                  <div key={c} data-testid={`layout-court-${c}`}
                    className={`border p-4 flex flex-col min-h-[150px] ${live ? "border-[#A3E635] bg-[#A3E635]/5" : "border-zinc-800 bg-zinc-950"}`}>
                    {item.medal && (
                      <div data-testid={`layout-medal-${c}`}
                        className={`text-[9px] font-black uppercase tracking-[0.25em] mb-1 ${item.medal === "championship" ? "text-yellow-400" : "text-[#22D3EE]"}`}>
                        {item.medal === "championship" ? "🏆 Championship Court" : "⭐ Center Court"}
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-display font-black text-3xl leading-none ${live ? "text-[#A3E635]" : "text-zinc-700"}`}>{c}</span>
                      {live ? (
                        <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#A3E635]">
                          <span className="w-2 h-2 rounded-full bg-[#A3E635] animate-pulse" /> Live
                        </span>
                      ) : <span className="text-[10px] font-black uppercase tracking-widest text-zinc-700">Open</span>}
                    </div>
                    {assigned.length > 0 && (
                      <div className="text-[9px] uppercase tracking-widest text-amber-400 mb-1 leading-snug" data-testid={`layout-court-${c}-divisions`}>{assigned.join(" · ")}</div>
                    )}
                    {live ? (
                      <div className="flex-1">
                        <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-0.5">{item.playing.label}</div>
                        <div className="font-display font-bold uppercase text-sm leading-tight">{item.playing.side_a}</div>
                        <div className="text-[9px] uppercase tracking-widest text-zinc-600">vs</div>
                        <div className="font-display font-bold uppercase text-sm leading-tight">{item.playing.side_b}</div>
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-zinc-800 font-display font-black uppercase text-xs">Court Open</div>
                    )}
                    {item.on_deck && (
                      <div className="mt-2 pt-2 border-t border-[#22D3EE]/30">
                        <div className="text-[9px] font-black uppercase tracking-[0.2em] text-[#22D3EE] mb-0.5">On Deck</div>
                        <div className="text-[11px] text-zinc-300 leading-snug">{item.on_deck.side_a} <span className="text-zinc-600">vs</span> {item.on_deck.side_b}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {divsWith.length === 0 ? (
          <div className="text-center text-zinc-600 py-16 font-display uppercase text-xl">No brackets generated yet.</div>
        ) : divsWith.map((d) => {
          const ms = matches.filter((m) => inDiv(m, d));
          const done = ms.filter((m) => m.status === "complete").length;
          const liveCount = ms.filter((m) => m.status !== "complete" && m.court).length;
          const assigned = ca[d.label] || [];
          return (
            <section key={d.label} data-testid={`layout-division-${slug(d.label)}`}>
              <div className="flex flex-wrap items-center gap-3 mb-3 border-b border-zinc-800 pb-2">
                <h2 className="font-display font-black uppercase text-xl leading-none">{d.label}</h2>
                {assigned.length > 0 && (
                  <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 border border-amber-500/40 px-2 py-0.5">
                    Courts {assigned.join(", ")}
                  </span>
                )}
                {liveCount > 0 && (
                  <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#A3E635]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A3E635] animate-pulse" /> {liveCount} on court
                  </span>
                )}
                <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-zinc-500">{done}/{ms.length} played</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
                {ms.map((m) => <MatchCard key={m.key} m={m} nameOf={nameOf} heldIds={heldIds} />)}
              </div>
            </section>
          );
        })}
      </main>

      <footer className="px-6 py-3 border-t border-zinc-800 text-[11px] text-zinc-600 flex justify-between">
        <span>Pickle Bullpen · TD Full Court Layout</span>
        <span>{board.stats.pending_scores > 0 ? `${board.stats.pending_scores} score${board.stats.pending_scores === 1 ? "" : "s"} awaiting TD approval` : "All scores up to date"}</span>
      </footer>
    </div>
  );
}
